/**
 * Route-level data prefetch configuration for the nav bar.
 *
 * Maps navigation paths to the TanStack Query prefetches they need.
 * Query keys and functions match the hooks each page uses, so prefetched
 * data is served directly from cache when the page mounts.
 *
 * Only routes reachable from the primary nav are configured.
 * Unlisted routes degrade gracefully — prefetchRoute() is a no-op.
 */
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';

import { sessionKeys } from '@/shared/api/hooks/useStudySessions';
import { enrolmentKeys } from '@/shared/api/hooks/useEnrolments';
import { statsKeys } from '@/shared/api/hooks/useStats';
import { reviseKeys } from '@/shared/api/hooks/useRevise';
import { courseKeys } from '@/shared/api/hooks/useCourses';
import { profileKeys } from '@/shared/api/hooks/useProfile';

import type { StudySessionWithSteps } from '@/shared/api/hooks/useStudySessions';
import type { EnrolmentWithCourse } from '@formency/shared';
import type { StatsResponse, PaginatedResponse, Course, CourseCategory } from '@formency/shared';
import type { RevisableContent } from '@/shared/api/hooks/useRevise';
import type { Profile } from '@/shared/api/hooks/useProfile';

interface PrefetchEntry {
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
  staleTime?: number;
}

const STALE_5M = 5 * 60_000;

const routePrefetches: Record<string, () => PrefetchEntry[]> = {
  '/': () => [
    {
      queryKey: sessionKeys.active(),
      queryFn: () =>
        api.get<StudySessionWithSteps | null>('/me/study-sessions/active', {
          include: 'steps,responses',
        }),
      staleTime: 30_000,
    },
    {
      queryKey: enrolmentKeys.me(),
      queryFn: () => api.get<EnrolmentWithCourse[]>('/me/enrolments'),
      staleTime: STALE_5M,
    },
    {
      queryKey: statsKeys.me(),
      queryFn: () => api.get<StatsResponse>('/me/stats'),
      staleTime: STALE_5M,
    },
  ],

  '/revision': () => [
    {
      queryKey: reviseKeys.content(),
      queryFn: () => api.get<RevisableContent>('/me/study-sessions/revisable-content'),
      staleTime: 60_000,
    },
    {
      queryKey: statsKeys.me(),
      queryFn: () => api.get<StatsResponse>('/me/stats'),
      staleTime: STALE_5M,
    },
  ],

  '/discover': () => [
    {
      queryKey: courseKeys.list({
        featured: true,
        pageSize: 5,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      }),
      queryFn: () =>
        api.get<PaginatedResponse<Course>>('/courses', {
          featured: true,
          pageSize: 5,
          sortBy: 'updatedAt',
          sortOrder: 'desc',
        }),
      staleTime: STALE_5M,
    },
    {
      queryKey: courseKeys.categories(),
      queryFn: () =>
        api.get<PaginatedResponse<CourseCategory>>('/courses/categories', { pageSize: 100 }),
      staleTime: 10 * 60_000,
    },
  ],

  '/my-courses': () => [
    {
      queryKey: enrolmentKeys.me(),
      queryFn: () => api.get<EnrolmentWithCourse[]>('/me/enrolments'),
      staleTime: STALE_5M,
    },
  ],

  // /create — no initial queries (form UI), intentionally omitted

  '/settings': () => [
    {
      queryKey: profileKeys.me(),
      queryFn: () => api.get<Profile>('/me/profile'),
      staleTime: STALE_5M,
    },
  ],
};

/** Prefetch data for a navigation target. No-op for unconfigured routes. */
export function prefetchRoute(queryClient: QueryClient, path: string): void {
  const factory = routePrefetches[path];
  if (!factory) return;

  for (const { queryKey, queryFn, staleTime } of factory()) {
    void queryClient.prefetchQuery({ queryKey, queryFn, staleTime });
  }
}

/** Routes to eagerly prefetch on shell mount (high-traffic nav destinations). */
const EAGER_ROUTES = ['/', '/revision', '/discover'] as const;

/**
 * Eagerly prefetch data for the primary nav routes after a short delay.
 * Called once on shell mount so data is cached before the user clicks.
 * The delay avoids competing with the current page's critical queries.
 */
export function prefetchPrimaryRoutes(queryClient: QueryClient): void {
  for (const route of EAGER_ROUTES) {
    prefetchRoute(queryClient, route);
  }
}
